import { Injectable } from '@angular/core';
import { IcurationWorklistItem } from '../interfaces/curation-worklist-item.interface';
import { CurationSettings } from '../components/curation-dashboard/curation-setting';
import { ICurationItem } from '../interfaces/curation-item.interface';
import { get } from 'lodash';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { NodeCreationResult } from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';
import { TreatmentPlanSelectorResult } from '@ispm-client/modules/bpmn-modeler/models/treatment-plan-selector-result';
import { cloneDeep } from 'lodash';

@Injectable()
export class CurationUtils {
  public static readonly ItemHasNewerInProcessRevision = 'hasNewerInProcessRevision';

  private static adaptCurationDataToGridInternal(item: IcurationWorklistItem, currentUserOrgId: string, isActionEnabled: boolean) {
    item.status = this.getCurationStatusDisplayString(item.status);

    // A bypass: notes can be configured to show different title BUT the config is per grid (and not per item).
    // Notes title is configured to be taken from 'aberrationKey' which exists only for aberrations.
    // So taking the 'name' and copy it to 'aberrationKey' in non-aberration types
    if (item.type != CurationSettings.CURATION_ABERRATION) {
      if ((!item.aberrationKey || item.aberrationKey == '' || item.aberrationKey == 'aberrationKey') && item['name']) {
        item.aberrationKey = item['name'];
      }
    }
    if (!item.lastUpdated) { // date pipe (in the grid) throws error if date is empty. Null should be passed instead.
      item.lastUpdated = null;
    }
    item.isActionEnabled = isActionEnabled;

    // in case that user does not belong to the org that created the item , user should not see the notes
    if (item['createOrg'] != currentUserOrgId) {
      item['cantEditNotes'] = true;
    }
    item['relatedEntitiesToUpdate'] = item['relatedEntitiesToUpdate'] && item['relatedEntitiesToUpdate'] > 0 ? 'Yes' : 'No';
    if (item['requiresUpdate'] !== undefined) {
      item['requiresUpdate'] = item['requiresUpdate'] ? 'Yes' : 'No';
    }
  }

  public static adaptCurationDataToGrid(item: IcurationWorklistItem, currentConf: any, currentUserOrgId: string) {
    let isActionEnabled: boolean = currentConf['ui']['cloneableTypes'] ?  currentConf['ui']['cloneableTypes'].indexOf(item.type ) > -1 : false;
    return this.adaptCurationDataToGridInternal(item, currentUserOrgId, isActionEnabled);
  }

  public static adaptDataToGridWithGrantedAction(item: IcurationWorklistItem, currentUserOrgId: string, isActionGranted: boolean) {
    return this.adaptCurationDataToGridInternal(item, currentUserOrgId, isActionGranted);
  }

  public static isTempStatus(status): boolean {
    return status === CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED || status === CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED;
  }

  public static getCurationStatusDisplayString(status) {
    return CurationItemStatusText[status] ? CurationItemStatusText[status] : '' ;
  }

  public static buildOpinion(currentUser, text: string = '', rejectMessage = '') {
    const opinion = {
      'practitioner': currentUser.id,
      'type': currentUser.info.practitionerRole['0'].role.display,
      'value': new Date(),
      'prefix': currentUser.info.name[0].prefix,
      'name': currentUser.info.name[0].text
    };
    if (text) {
      opinion['text'] = text;
    }
    if (rejectMessage) {
      opinion['rejectMessage'] = rejectMessage;
    }
    return opinion;
  }

  public static getFlatAberrationData(curationItem: ICurationItem): { [key: string]: string; } {
    // collects relevant aberration data
    const data: { [key: string]: string; } = { };
    if (curationItem && curationItem.curationData) {
      data['disease'] = curationItem.diseaseDisplay;
      data['AberrationType'] = curationItem.aberration.AberrationClass;
      data['gene'] = curationItem.aberration.gene;
      data['AAChange'] = curationItem.aberration.AAChange;
      data['icdo3Diagnosis'] = curationItem.diseaseDisplay;
      data['additionalCurationProperties'] = get(curationItem, 'curationData.additionalCurationProperties');

      if (curationItem.curationData['manualAnnotations']) {
        Object.assign(data, curationItem.curationData['manualAnnotations']);
      }
      if (curationItem.annotations) {
        for (let i = 0; i < curationItem.annotations.length; i++) {
          const annotationSource = curationItem.annotations[i];
          for (let j = 0; j < annotationSource['annotations'].length; j++) {
            const annotation = annotationSource['annotations'][j];
            data[annotation['name']] = annotation['value'];
          }
        }
      }
      if (curationItem.aberration) {
        Object.assign(data, curationItem.aberration);
      }
    }
    return data;
  }

  /*
  Copy curation relevant (configured) data to curationData\relatedEntities object. The server will use this data
  to determine if related entities were changed.
  */
  public static updateRelatedEntities(conf: any, curationData: any,
      relatedEntitiesFieldsArray: any = null, curationCreation: boolean = false,
      forceUpdateRelatedEntities?: boolean){
    const relatedEntitiesRoot = conf['ui']['relatedEntitiesCreation'];
    if (relatedEntitiesRoot && curationData.hasOwnProperty('content')) {
      const curationRelatedEntitiesRoot = relatedEntitiesRoot['containedCuration' + '_' + curationData['content']['type']]; // params rootname is containedCuration_curationType. e.g.: containedCuration_pathwaybpmn
      if (!curationRelatedEntitiesRoot) {
        return;
      }

      const updateOnCreationParam = relatedEntitiesRoot['updateOnCreation'][curationData['content']['type']]; // updateOnCreationParam means that it is a new creation item (was called form the dashboard) and no related entities were saved before
      if (curationCreation && updateOnCreationParam != undefined && updateOnCreationParam == false) {
        return; // for curation items that do no create related entities at creation but only on update (like pathwayBPMN)
      }

      // Method can get a root that contains related entities or an array of related entities
      let relatedEntitySourceArray = [];
      if (relatedEntitiesFieldsArray != null && relatedEntitiesFieldsArray != undefined) { // in case related entities array was passed
        relatedEntitySourceArray = relatedEntitiesFieldsArray;
      }
      else { // in case root was passed, looking for the related entities within this root and adding them to an array
        const sourceRootObj: any = get(curationData, relatedEntitiesRoot['sourceRoot']);
        for (const item of Object.keys(sourceRootObj)) {
          if (curationRelatedEntitiesRoot.find(entity => entity['reviseItemType'] == '$' + item)) { // Adding item to the array if it is a related entity (means has a related entity config node). Item type appears has '$' prefix. e.g.: "$regimen"
            const elem = { };
            elem[item] = sourceRootObj[item];
            relatedEntitySourceArray.push(elem);
          }
        }
      }
      if (relatedEntitySourceArray.length === 0) {
        // In case related data were exist and deleted, deleting destination root
        delete curationData[relatedEntitiesRoot['destRoot']];
        return;
      }

      // Going through all related entities elements
      for (const sourceEntityType of relatedEntitySourceArray) {
        const sourceEntity = sourceEntityType[Object.keys(sourceEntityType)[0]]; // related entity curation item type is the object root name
        const itemType: string = Object.keys(sourceEntityType)[0];
        const entityConfigRoot = curationRelatedEntitiesRoot.find(entity => entity['reviseItemType'] == '$' + itemType); // looking for the related entity configof the item curation type

        if (entityConfigRoot) {
          if (!curationData[relatedEntitiesRoot['destRoot']]) {
            curationData[relatedEntitiesRoot['destRoot']] = [];
          }
          let isNewEntity: boolean = !(!!sourceEntity['relatedEntityId']);

          let destRelatedEntity = {};
          if (isNewEntity || forceUpdateRelatedEntities){
            destRelatedEntity['relatedEntityId'] = StringUtils.generateGuid();
            sourceEntity['relatedEntityId'] = destRelatedEntity['relatedEntityId'];
            curationData[relatedEntitiesRoot['destRoot']].push(destRelatedEntity);
          }
          else {
            destRelatedEntity = curationData[relatedEntitiesRoot['destRoot']].find(item => item.relatedEntityId == sourceEntity.relatedEntityId);
            if (destRelatedEntity) {
              if (destRelatedEntity['latestWorkflowRevisionName'] === sourceEntity['revisionName'] || // If revision is the latest, erasing all entity properties except 'relatedEntityId' (it will be created without server - like 'upToData')
                  sourceEntity.code != destRelatedEntity['revisedCurationItemId']) { // if related item was changed (e.g. a different TP was selected, not just revision was changed)
                Object.keys(destRelatedEntity).forEach((key) =>  delete destRelatedEntity[key]);
                destRelatedEntity['relatedEntityId'] = sourceEntity['relatedEntityId'];
              }
            }
          }

          if (destRelatedEntity) {
            for (const itemName of Object.keys(entityConfigRoot)) {
              if (itemName === 'identifiers') {
                const identifierItem: any = [];
                for (const identifier of entityConfigRoot['identifiers']) {
                  const systemParam: string = identifier['system'];
                  const param: string = identifier['value'];
                  const valueParam: string =  param.startsWith('$') ?  param.substr(1) : sourceEntity[param];
                  identifierItem.push({ system: systemParam, value: valueParam });
                }
                destRelatedEntity['identifiers'] =  identifierItem;
              }
              else {
                const param: string = entityConfigRoot[itemName];
                destRelatedEntity[itemName] = param.startsWith('$') ?
                param.substr(1) : sourceEntity[param];
              }
            }
            if (sourceEntity['copyRelatedEntity']) {
             CurationUtils.handleRelatedEntityOfCopiedTR(sourceEntity, destRelatedEntity, relatedEntitiesRoot, curationData);
            }
          }
          else {
            console.error('curation-utils->updateRelatedEntities: cannot update related entities:' + itemType);
          }
        }
        else {
          console.warn('curation-utils->updateRelatedEntities: missing relateed entity configuration for:' + itemType);
        }
      }
      // removing deleted related entities (In pathwayBPMN, for example, TP are not mandatory)
      for (let i = curationData[relatedEntitiesRoot['destRoot']].length - 1; i >= 0; i--) {
        // If related entity server data was not found in the source array, remove it
        if (relatedEntitySourceArray.findIndex(item => item[Object.keys(item)[0]].relatedEntityId == curationData[relatedEntitiesRoot['destRoot']][i].relatedEntityId) === -1) {
          curationData[relatedEntitiesRoot['destRoot']].splice(i, 1);
        }
      }
    }
  }

  public static  handleRelatedEntityOfCopiedTR (newTr: any, relatedEntityToCopy: any , relatedEntitiesRoot: any , curationData: any) {
    const newRelatedEntity = cloneDeep(relatedEntityToCopy);
    const newId: string = StringUtils.generateGuid();
    newRelatedEntity['relatedEntityId'] = newTr['relatedEntityId'] = newId;
    curationData[relatedEntitiesRoot['destRoot']].push(newRelatedEntity);
    newTr['copyRelatedEntity'] = false;
  }

  public static setCopyRelatedEntity(data: NodeCreationResult | TreatmentPlanSelectorResult): TreatmentPlanSelectorResult | NodeCreationResult {

    if (data && 'treatmentPlans' in data) {
      const treatmentPlans = data.treatmentPlans;
      treatmentPlans.forEach(treatment => {
        if (treatment.treatmentPlan) {
          treatment.treatmentPlan['copyRelatedEntity'] = true;
        }
      });
      data.treatmentPlans = treatmentPlans;
    }
    return data;

  }

  public static setEvidenceDisplayString(item: IcurationWorklistItem) {
    const BENIGN_STR = 'BENIGN';
      if (item.type == CurationSettings.CURATION_ABERRATION) {
      if (item['diagnosticMaxEvidLevel'] && item['diagnosticMaxEvidLevel'].toUpperCase() == BENIGN_STR) {
        item['diagnosticMaxEvidLevel'] = 'V';
      }
      if (item['prognosticMaxEvidLevel'] && item['prognosticMaxEvidLevel'].toUpperCase() == BENIGN_STR) {
        item['prognosticMaxEvidLevel'] = 'V';
      }
      if (item['therapeuticMaxEvidLevel'] && item['therapeuticMaxEvidLevel'].toUpperCase() == BENIGN_STR) {
        item['therapeuticMaxEvidLevel'] = 'V';
      }
    }
  }

  /**
   * Removing duplicated links from pubMed data
   *
   * @param pubmedLinks
   */
  public static removeDuplicatePubmed(pubmedLinks: any): any {
    const allUniquePubmeds = new Set();

    // removing duplicate pubmeds ID (link)
    return pubmedLinks.filter(pubmeds => {
      const link = pubmeds.pubmedlink ? pubmeds.pubmedlink.trim() : pubmeds.pubmedlink;

      if (link === undefined) {
        return true;
      }

      const check = !allUniquePubmeds.has(link);
      allUniquePubmeds.add(link);

      return check;
    });
  }

  /**
   * Removing undefined links from pubMed data
   *
   * @param pubmedLinks
   */
  public static removeUndefinedPubmed(pubmedLinks: any): any {
    return pubmedLinks.filter(pubmeds => {
      return pubmeds.pubmedlink ? pubmeds.pubmedlink.trim() : pubmeds.pubmedlink;
    });
  }
}

enum CurationItemStatusText {
  draft = 'In revision',
  approved = 'Approved',
  published = 'Published',
  waitforapproval = 'Pending approval',
  waitforarchive = 'Pending for archive',
  rejected = 'Rejected',
  archived = 'Archived',
  inreview = 'In review',
  reviewFeedbackReceived = 'In review',
  publishedsubmitted = 'Publish in Progress',
  archivedsubmitted = 'Archive in Progress',
  approvedsubmitted = 'Publish in Progress'
}
